<template>
  <div style="position:absolute;top:0;left:0;right:0;bottom:0;" class="layout-column">
    <div class="layout-column flex ma-2 elevation-5"
         style="border-radius:4px;position:relative;"
         v-bind:style="{background: compColor.contentFrame}">

      <ValidationObserver ref="NewsDetailForm" v-slot="{ valid }" class="layout-column flex">

        <div v-bind:style="{'z-index': '4'}" class="layout-row layout-align-center-center pa-1">
          <div class="layout-row">
            <lx-btn small classes="mr-1"
                    @click="back(true)"
                    icon="chevron_left"
                    :tooltip="$t('NEWS_DETAILS.BUTTONS.BACK')"
                    hide-label>
            </lx-btn>
            <lx-btn v-if="model.isEditable"
                    small classes="mr-1"
                    @click="saveData"
                    icon="save"
                    :tooltip="$t('NEWS_DETAILS.BUTTONS.SAVE')"
                    :disabled="isPristine || !valid || errorsEmptyDownloads.length > 0 || (model.contentElements.length === 0 && model.actions.length === 0)"
                    hide-label>
            </lx-btn>
          </div>
          <div class="flex layout-row"></div>
        </div>

        <v-divider v-bind:style="{'border-color':compColor.lightDivider}"></v-divider>

        <div class="flex layout-column pa-2" style="overflow-y: auto;">
          <v-progress-linear
            v-if="loading"
            :indeterminate="loading"
            color="red accent-2"
            background-color="red lighten-4"
            class="ma-0"
            style="border-radius:0;height:4px;position: absolute;top:56px;left:0; width:100%;">
          </v-progress-linear>

          <lx-expansion-panels :single-open="false" v-model="panel">
            <lx-expansion-panel disabled>
              <lx-expansion-panel-header
                icon="mdi-newspaper-variant-outline"
                disabled
                :main-headline="$t('NEWS_DETAILS.HEADER')"
                :sub-headline="$t('NEWS_DETAILS.SUBHEADING')">
              </lx-expansion-panel-header>

              <lx-expansion-panel-content>
                <div class="flex pa-2 layout-column"
                     style="overflow-y: hidden;"
                     v-bind:class="[compColor.contentFrameBack]">
                  <div class="flex pa-3">
                    <div class="mb-4">
                      <validation-provider ref="headline"
                                           tag="div"
                                           name="headline"
                                           rules="required"
                                           v-slot="{ errors }">
                        <v-text-field
                          :dark="compColor.isInDarkMode"
                          :color="compColor.primeColor"
                          :dense="denseFields"
                          :label="$t('NEWS_DETAILS.FIELDS.HEADLINE') + '*'"
                          placeholder=" "
                          outlined
                          clearable
                          :error-messages="errors[0]"
                          :hide-details="!errors || errors.length <= 0"
                          v-model="model.headline"
                          prepend-inner-icon="assignment"
                          class="mb-3"
                          :disabled="!model.isEditable"
                        ></v-text-field>
                      </validation-provider>
                    </div>

                    <div class="mb-4">
                      <validation-provider ref="subheading"
                                           tag="div"
                                           name="subheading"
                                           v-slot="{ errors }">
                        <v-text-field
                          :dark="compColor.isInDarkMode"
                          :color="compColor.primeColor"
                          :dense="denseFields"
                          :label="$t('NEWS_DETAILS.FIELDS.SUBHEADING')"
                          placeholder=" "
                          outlined
                          clearable
                          :error-messages="errors[0]"
                          :hide-details="!errors || errors.length <= 0"
                          v-model="model.subheading"
                          prepend-inner-icon="comment"
                          class="mb-3"
                          :disabled="!model.isEditable"
                        ></v-text-field>
                      </validation-provider>
                    </div>

                    <div class="layout-row layout-wrap" v-if="!model.isWelcomeNews">
                      <div class="flex-gt-sm-20 pb-0">
                        <validation-provider tag="div"
                                             ref="validFromDate"
                                             name="validFromDate"
                                             rules="required|german_date"
                                             v-slot="{ errors }">
                          <lx-datepicker
                            :dark="compColor.isInDarkMode"
                            :color="compColor.primeColor"
                            :error="errors[0]"
                            :label="$t('NEWS_DETAILS.VALID_FROM_DATE') + ' *'"
                            class="mr-1"
                            :dense="denseFields"
                            v-model="model.validFromDate"
                            :disabled="!model.isEditable"
                          ></lx-datepicker>
                        </validation-provider>
                      </div>

                      <div class="flex-gt-sm-20 pl-5 pb-0">
                        <validation-provider tag="div"
                                             ref="validToDate"
                                             name="validToDate"
                                             rules="german_date"
                                             v-slot="{ errors }">
                          <lx-datepicker
                            :dark="compColor.isInDarkMode"
                            :color="compColor.primeColor"
                            :error="errors[0]"
                            :label="$t('NEWS_DETAILS.VALID_TO_DATE')"
                            class="mr-1"
                            :dense="denseFields"
                            v-model="model.validToDate"
                            :disabled="!model.isEditable"
                          ></lx-datepicker>
                        </validation-provider>
                      </div>
                    </div>

                    <div class="layout-row layout-wrap" v-if="!model.isWelcomeNews">
                      <div class="flex-gt-sm-20 pr-1 pb-0 mb-4">
                        <validation-provider tag="div"
                                             ref="roles"
                                             name="roles"
                                             rules="required"
                                             v-slot="{ errors }">
                          <v-select
                            class="ma-0"
                            :color="compColor.primeColor"
                            v-model="model.roles"
                            :items="roles"
                            :menu-props="{ bottom: true, offsetY: true }"
                            :background-color="compColor.inputs"
                            :dark="compColor.isInDarkMode"
                            :placeholder="$t('COMPONENT_DASHBOARD.ALL')"
                            :label="$t('NEWS_DETAILS.PERMISSIONS') + ' *'"
                            :disabled="!model.isEditable"
                            outlined
                            multiple
                            dense
                            :error="errors[0]"
                            :hide-details="!errors || errors.length <= 0">

                            <template #item="{item, on, attrs}">
                              <v-list-item v-on="on">
                                <v-list-item-action>
                                  <v-simple-checkbox
                                    :value="attrs.inputValue"
                                    v-on="on"
                                    :color="compColor.primeColor"
                                    :dark="compColor.isInDarkMode"
                                    :ripple="false"
                                  ></v-simple-checkbox>
                                </v-list-item-action>

                                <v-list-item-content>
                                  <v-list-item-title>
                                    <label :style="{ color: attrs.inputValue ? compColor.primeColor : '#546E7A'}">{{ $t('DOWNLOADS.ROLES.' + item) }}</label>
                                  </v-list-item-title>
                                </v-list-item-content>
                              </v-list-item>
                            </template>

                            <template #selection="item">
                              {{ formatRoleListElement(item) }}
                            </template>
                          </v-select>
                        </validation-provider>
                      </div>
                    </div>

                    <div class="layout-row flex-gt-sm-30 pb-0" v-if="userIsRoot">
                      <v-switch
                        v-model="model.isWelcomeNews"
                        class="ml-2 mb-4 my-0 py-0"
                        dense
                        inset
                        :color="compColor.prime"
                        :dark="isDarkMode"
                        hide-details
                        @change="toggleIsWelcomeNews"
                      >
                        <template #label>
                          <span class="body-2 blue-grey--text text--lighten-2">{{ $t('COMPONENT_DASHBOARD.NEWS_IS_WELCOME_NEWS') }}</span>
                        </template>
                      </v-switch>
                    </div>

                    <!-- contentElements -->

                    <div>
                      <div class="layout-row">
                        <lx-btn width="auto"
                                :height="35"
                                iconSize="21"
                                :color="compColor.primeColor"
                                :iconColor="'white'"
                                @click="addContentElement"
                                classes="text-none mr-1 mb-2 mt-3"
                                depressed
                                :tooltip="$t('NEWS_DETAILS.BUTTONS.ADD_CONTENT_ELEMENT')"
                                :label="$t('NEWS_DETAILS.BUTTONS.ADD_CONTENT_ELEMENT')"
                                icon="mdi-home-plus"
                                :disabled="!model.isEditable">
                        </lx-btn>
                        <span class="ml-5 mt-4" :style="{color: compColor.primeColor}" style="font-style: italic">{{ $t('COMPONENT_DASHBOARD.NEWS_TEXT_EDIT_CONTENT') }}</span>
                      </div>

                      <div :key="index"
                           v-for="(item, index) in model.contentElements"
                           class="layout-row pa-0 my-2 elevation-2"
                           :class="{'elevation-5': compColor.isInDarkMode, 'grey lighten-3': !compColor.isInDarkMode}"
                           style="border-radius:6px;"
                           :style="{'border': compColor.isInDarkMode ? '1px solid #cccccc22 !important' : '1px solid #ccc !important;' }">

                        <div
                          :draggable="model.isEditable"
                          @dragstart="startRowDrag(item)"
                          @dragend="endRowDrag"
                          @dragenter="checkRowCollision(model.contentElements, item)"
                          class="flex layout-row"
                          :class="[item.dragDropClass, {'grey lighten-3': item.dragDropClass && item.dragDropClass.indexOf('amber') < 0}]">

                          <div class="layout-column layout-align-center-center px-3"
                               :class="{'grey lighten-2': !compColor.isInDarkMode}"
                               style="border-radius: 6px 0 0 6px;"
                               :style="{'border-left': compColor.isInDarkMode ? '1px solid #cccccc22 !important' : '1px solid #ccc !important;' }">

                            <v-icon size="20" :class="[compColor.isInDarkMode ? 'blue-grey--text' : 'blue-grey--text text--lighten-1']">
                              mdi-swap-vertical
                            </v-icon>
                          </div>

                          <div class="layout-row flex pt-2 px-3 layout-wrap">
                            <div class="flex-gt-sm-50 flex-100 pa-2 pb-0">
                              <validation-provider tag="div"
                                                   :name="'contentElements.' + index + '.title'"
                                                   :ref="'contentElements.' + index + '.title'"
                                                   rules="min:2"
                                                   v-slot="{ errors }">
                                <v-text-field
                                  :dark="compColor.isInDarkMode"
                                  :color="compColor.primeColor"
                                  :dense="denseFields"
                                  :counter="100"
                                  outlined
                                  :error-messages="errors[0]"
                                  :label="$t('NEWS_DETAILS.CONTENT_ELEMENTS.TITLE')"
                                  v-model="item.title"
                                  :disabled="!model.isEditable"
                                ></v-text-field>
                              </validation-provider>
                            </div>

                            <div class="flex-gt-sm-50 flex-100 pa-2 pb-0">
                              <validation-provider tag="div"
                                                   :name="'contentElements.' + index + '.value'"
                                                   :ref="'contentElements.' + index + '.value'"
                                                   rules="required"
                                                   v-slot="{ errors }">
                                <v-text-field
                                  :dark="compColor.isInDarkMode"
                                  :color="compColor.primeColor"
                                  :dense="denseFields"
                                  :counter="2000"
                                  outlined
                                  :error-messages="errors[0]"
                                  :label="$t('NEWS_DETAILS.CONTENT_ELEMENTS.VALUE') + ' *'"
                                  v-model="item.value"
                                  :disabled="!model.isEditable"
                                ></v-text-field>
                              </validation-provider>
                            </div>

                          </div>

                          <div class="layout-column layout-align-center-center px-3"
                               :class="{'grey lighten-2': !compColor.isInDarkMode}"
                               style="border-radius: 0 6px 6px 0;"
                               :style="{'border-left': compColor.isInDarkMode ? '1px solid #cccccc22 !important' : '1px solid #ccc !important;' }">
                            <lx-btn @click="deleteContentElement(index)"
                                    :color="compColor.primeColor"
                                    :iconColor="'white'"
                                    iconSize="20"
                                    :width="35"
                                    :height="35"
                                    icon="delete_forever"
                                    :disabled="!model.isEditable"></lx-btn>
                          </div>
                        </div>
                      </div>
                    </div>

                    <v-divider class="my-5"/>

                    <!-- actions -->

                    <div>
                      <lx-btn classes="text-none mr-1 mb-2"
                              width="auto"
                              :height="35"
                              iconSize="21"
                              :color="compColor.primeColor"
                              :iconColor="'white'"
                              @click="addAction"
                              depressed
                              :tooltip="$t('NEWS_DETAILS.BUTTONS.ADD_ACTION')"
                              :label="$t('NEWS_DETAILS.BUTTONS.ADD_ACTION')"
                              icon="mdi-home-plus"
                              :disabled="!model.isEditable">
                      </lx-btn>

                      <div :key="index"
                           v-for="(item, index) in model.actions"
                           class="layout-row pa-0 my-2 elevation-2"
                           :class="{'elevation-5': compColor.isInDarkMode, 'grey lighten-3': !compColor.isInDarkMode}"
                           style="border-radius: 6px;"
                           :style="{'border': compColor.isInDarkMode ? '1px solid #cccccc22 !important' : '1px solid #ccc !important;' }">

                        <div
                          :draggable="model.isEditable"
                          @dragstart="startRowDrag(item)"
                          @dragend="endRowDrag"
                          @dragenter="checkRowCollision(model.actions, item)"
                          class="flex layout-row"
                          :class="[item.dragDropClass, {'grey lighten-3': item.dragDropClass && item.dragDropClass.indexOf('amber') < 0}]">

                          <div class="layout-column layout-align-center-center px-3"
                               :class="{'grey lighten-2': !compColor.isInDarkMode}"
                               style="border-radius: 6px 0 0 6px;"
                               :style="{'border-left': compColor.isInDarkMode ? '1px solid #cccccc22 !important' : '1px solid #ccc !important;' }">

                            <v-icon size="20" :class="[compColor.isInDarkMode ? 'blue-grey--text' : 'blue-grey--text text--lighten-1']">
                              mdi-swap-vertical
                            </v-icon>
                          </div>

                          <div class="layout-row flex pt-2 px-3 layout-wrap">

                            <!-- selection for action type -->

                            <div class="flex-gt-sm-20 flex-100 pa-2 pb-0">
                              <validation-provider tag="div"
                                                   :name="'availableActions.' + index + '.action'"
                                                   :ref="'availableActions.' + index + '.action'"
                                                   rules="required|min:2"
                                                   v-slot="{ errors }">
                                <v-select
                                  :dark="compColor.isInDarkMode"
                                  :color="compColor.primeColor"
                                  :dense="denseFields"
                                  outlined
                                  @change="setErrorElementForEmptyDownload(item.action, index)"
                                  :label="$t('NEWS_DETAILS.ACTIONS.ACTION') + ' *'"
                                  :items="availableActions"
                                  :hide-details="!errors || errors.length <= 0"
                                  :error-messages="errors[0]"
                                  v-model="item.action"
                                  :disabled="!!(model._id && item.action==='download' && item.path) || !model.isEditable"
                                  :menu-props="{ bottom: true, offsetY: true }">
                                  <template v-slot:item="{ item, on, attrs }">
                                    <v-list-item v-on="on"
                                                 :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                                      <v-list-item-content class="py-0">
                                        <label style="cursor:pointer;"
                                               :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                               class="lighten-4 text--darken-2">{{ item.text }}</label>
                                      </v-list-item-content>
                                    </v-list-item>
                                  </template>
                                </v-select>
                              </validation-provider>
                            </div>

                            <!-- param for action e.g. download url or link -->

                            <div class="flex-gt-sm-80 flex-100 pa-2 pb-0">
                              <validation-provider tag="div"
                                                   :name="'availableActions.' + index + '.param'"
                                                   :ref="'availableActions.' + index + '.param'"
                                                   :rules="{required: !!(item.action && (item.action==='link' || item.action==='method'))}"
                                                   v-if="item.action && (item.action==='link' || item.action==='method')"
                                                   v-slot="{ errors }">
                                <v-text-field
                                  :dark="compColor.isInDarkMode"
                                  :color="compColor.primeColor"
                                  :dense="denseFields"
                                  :counter="200"
                                  outlined
                                  :hide-details="!errors || errors.length <= 0"
                                  :error-messages="errors[0]"
                                  :label="$t('NEWS_DETAILS.ACTION_PARAM.'+item.action.toUpperCase()) + ' *'"
                                  v-model="item.param"
                                  :disabled="!model.isEditable"
                                ></v-text-field>
                              </validation-provider>

                              <!-- action 'download' -->

                              <div class="layout-row" v-if="item.action && item.action==='download'">
                                <v-btn
                                  v-if="!item.filename"
                                  @click="openHiddenFileInput(index)"
                                  :color="compColor.primeColor"
                                  :dark="compColor.isInDarkMode"
                                  :width="300"
                                  :height="denseFields ? 39 : 48"
                                  :disabled="!!(model._id && item.action==='download' && item.path) || !model.isEditable"
                                  class="white--text text-none pa-4">
                                  {{ $t('COMPONENT_DASHBOARD.INFO_CHOOSE_FILE_SINGLE') }}
                                </v-btn>

                                <input type="file" hidden :ref="'hiddenFileInput' + index" @change="addFilesByDialog(index)"/>

                                <span class="pt-2 ml-4"
                                      v-if="!item.filename"
                                      :style="{color: compColor.primeColor}"
                                      style="font-style: italic">
                                {{ $t('COMPONENT_DASHBOARD.INFO_CHOOSE_FILE_TEXT') }}</span>

                                <span class="pa-2" v-if="item.filename"
                                      :style="{color: compColor.primeColor}"
                                      style="font-style: italic">{{ $t('ACTIVITIES.FILENAME') }}: {{ item.filename }}</span>
                              </div>

                              <!-- action 'confirm' -->

                              <div class="layout-row" v-if="item.action && item.action==='confirm'">
                                <span class="px-2"
                                      :style="{color: compColor.primeColor}"
                                      style="font-style: italic">{{ $t('NEWS_DETAILS.ACTIONS.CONFIRM_TEXT_EDIT_MODE') }}</span>
                              </div>
                            </div>

                            <!-- action name, is text for button of action -->

                            <div class="offset-20 flex-gt-sm-80 flex-100 px-2 pb-0" v-if="item.action">
                              <validation-provider tag="div"
                                                   :name="'availableActions.' + index + '.name'"
                                                   :ref="'availableActions.' + index + '.name'"
                                                   :rules="{required: !!item.action}"
                                                   v-if="item.action"
                                                   v-slot="{ errors }">
                                <v-text-field
                                  :dark="compColor.isInDarkMode"
                                  :color="compColor.primeColor"
                                  :dense="denseFields"
                                  :counter="200"
                                  outlined
                                  :hide-details="!errors || errors.length <= 0"
                                  :error-messages="errors[0]"
                                  :label="$t('NEWS_DETAILS.ACTIONS.NAME') + ' *'"
                                  v-model="item.name"
                                  :disabled="!model.isEditable"
                                ></v-text-field>
                              </validation-provider>
                            </div>
                          </div>

                          <!-- delete button -->

                          <div class="layout-column layout-align-center-center px-3"
                               :class="{'grey lighten-2': !compColor.isInDarkMode}"
                               style="border-radius: 0 6px 6px 0;"
                               :style="{'border-left': compColor.isInDarkMode ? '1px solid #cccccc22 !important' : '1px solid #ccc !important;' }">
                            <lx-btn @click="deleteAction(index)"
                                    :color="compColor.primeColor"
                                    :iconColor="'white'"
                                    iconSize="20"
                                    :width="35"
                                    :height="35"
                                    icon="delete_forever"
                                    :disabled="!model.isEditable"></lx-btn>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </lx-expansion-panel-content>
            </lx-expansion-panel>
          </lx-expansion-panels>

          <lx-expansion-panels :single-open="false" v-model="panel">
            <lx-expansion-panel disabled>
              <lx-expansion-panel-header icon="mdi-incognito"
                                         disabled
                                         main-headline="Preview">
              </lx-expansion-panel-header>

              <lx-expansion-panel-content>
                <div class="flex pa-2 layout-column" style="overflow-y: hidden;"
                     v-bind:class="[compColor.contentFrameBack]">
                  <div class=" flex pa-3">
                    <div class="flex-50 layout-column px-3 elevation-2" v-bind:style="{'border-radius': '5px'}" v-bind:class="[compColor.widget.text]">

                      <!-- headline with subheading -->

                      <div class="font-weight-bold subtitle-1 my-1" style="line-height: 1.0rem;">{{ model.headline }}</div>
                      <div v-if="model.subheading" class="font-weight-regular my-1" style="line-height: 1.0rem;font-size: 14px;">{{ model.subheading }}</div>

                      <v-divider class="mb-1" v-bind:class="[compColor.divider]"></v-divider>

                      <!-- Content elements -->

                      <div v-if="model.contentElements">
                        <div v-for="(element, index) in model.contentElements" :key="index" class="font-weight-regular subtitle-2 my-1 layout-row" style="line-height: 1.0rem;">
                          <span class="flex-35" v-if="element.title">{{ element.title }}:</span>
                          <span class="ml-2 flex" v-html="element.value"></span>
                        </div>
                      </div>

                      <!-- Actions/Buttons -->

                      <div v-if="model.actions" class="font-weight-regular subtitle-2 mt-3 mb-2 layout-row">
                        <div v-for="(action, index) in model.actions" :key="index" class="layout-row flex">

                          <!-- link -->
                          <v-btn v-if="action.action==='link'"
                                 :href="action.param" target="_blank"
                                 :dark="compColor.isInDarkMode"
                                 class="white--text text-none ml-2 pa-5 flex"
                                 :color="compColor.primeColor"
                                 dense
                                 depressed>
                            {{ action.name }}
                          </v-btn>

                          <!-- download -->
                          <v-btn v-if="action.action === 'download'"
                                 :dark="compColor.isInDarkMode"
                                 class="white--text text-none ml-2 pa-5 flex"
                                 :color="compColor.primeColor"
                                 dense
                                 depressed>
                            {{ action.name }}
                          </v-btn>

                          <!-- method -->
                          <v-btn v-if="action.action === 'method'"
                                 :dark="compColor.isInDarkMode"
                                 class="white--text text-none ml-2 pa-5 flex"
                                 :color="compColor.primeColor"
                                 dense
                                 depressed>
                            {{ action.name }}
                          </v-btn>

                          <!-- confirm -->
                          <v-btn v-if="action.action === 'confirm'"
                                 :dark="compColor.isInDarkMode"
                                 class="white--text text-none ml-2 pa-5 flex"
                                 :color="compColor.primeColor"
                                 dense
                                 depressed>
                            {{ action.name }}
                          </v-btn>
                        </div>
                      </div>

                      <!-- Creation date -->

                      <span v-if="!model.isWelcomeNews"
                            class="text--lighten-2 font-weight-regular caption mb-2"
                            style="line-height: 1.0rem;"
                            :class="[compColor.textInput + '--text']">
                        {{ $t('NEWS.VALID_FROM_DATE') }} {{ model.validFromDate | formatDate('DD.MMM YYYY') }}
                      </span>
                    </div>
                  </div>
                </div>
              </lx-expansion-panel-content>
            </lx-expansion-panel>
          </lx-expansion-panels>
        </div>
      </ValidationObserver>
    </div>
  </div>
</template>
<script>
import lodash from 'lodash'
import axios from 'axios'

export default {
  name: 'NewsDetails',
  props: {
    newsId: {
      type: String
    },
    files: []
  },

  data () {
    return {
      loading: false,
      uploadingFile: false,
      model: {
        isWelcomeNews: false,
        roles: [],
        isEditable: true,
        contentElements: [],
        actions: []
      },
      oldModel: {},
      panel: [0, 1],
      errorsEmptyDownloads: [],
      availableActions: [
        { value: 'link', text: this.$t('NEWS_DETAILS.ACTION_TYPES.LINK') },
        { value: 'download', text: this.$t('NEWS_DETAILS.ACTION_TYPES.DOWNLOAD') },
        { value: 'confirm', text: this.$t('NEWS_DETAILS.ACTION_TYPES.CONFIRM') },
        { value: 'method', text: this.$t('NEWS_DETAILS.ACTION_TYPES.METHOD') }
      ],
      lockDragEnter: false,
      originalRow: null,
      movedRow: null,
      activeHoveredRow: null,
      roles: []
    }
  },

  created () {
    this.getData()

    // delete action "method" if user is sysadmin
    if (this.userIsSystemadmin && this.availableActions && Array.isArray(this.availableActions) && this.availableActions.length === 4) {
      this.availableActions.splice(3, 1)
    }
  },

  methods: {
    clearForm (reset) {
      reset()
      this.model = lodash.cloneDeep(this.oldModel)
      this.errorsEmptyDownloads = []
    },

    /**
     * Back to overview and delete temporary files if news is not saved
     *
     * @param cleanData
     */
    back (cleanData) {
      // remove all temporary uploaded files
      if (cleanData && Array.isArray(this.model.actions)) {
        this.model.actions.forEach((action) => {
          if (action.path && action.status === 'temporary') {
            this.deleteFile(action.path)
          }
        })
      }
      this.clearForm(this.$refs.NewsDetailForm.reset)

      // Trigger 'setShowDetails()' on parent
      this.$emit('set-show-details', false)
    },

    /**
     * Get selected news from db if exists
     */
    getData () {
      this.model.validFromDate = new Date()
      this.errorsEmptyDownloads = []

      this.$http.get('/documents/permissions')
        .then(res => {
          if (res.data && res.data) {
            this.roles = res.data
            this.model.roles = res.data

            if (this.newsId) {
              if (this.loading) return
              this.loading = true

              // get selected news
              this.$http.get('/dashboard/news/' + this.newsId)
                .then(res => {
                  if (res.data) {
                    this.model = res.data || []

                    // add initialId's to contentElements
                    this.model.contentElements.forEach((item, index) => {
                      if (!item.initialId && typeof item.initialId !== 'number') {
                        item.initialId = index
                      }
                    })

                    // add initialId's to actions
                    this.model.actions.forEach((item, index) => {
                      if (!item.initialId && typeof item.initialId !== 'number') {
                        item.initialId = index
                      }

                      if (item.action === 'download' && !item.path) {
                        this.errorsEmptyDownloads.push(index)
                      }
                    })
                    this.oldModel = lodash.cloneDeep(this.model)
                  }
                })
                .catch(this.NotifyError)
                .finally(() => {
                  this.loading = false
                })
            }
          }
        })
        .catch(() => {
          this.roles = []
        })
    },

    /**
     * Save selected news
     */
    saveData () {
      if (this.loading) return
      this.loading = true

      if (Array.isArray(this.model.roles) && this.model.roles.length === 0) {
        this.model.roles = this.roles
      }

      this.$http[!this.model._id ? 'post' : 'put']('/dashboard/news', this.model)
        .then(() => {
          this.NotifySystem(this.$t('NEWS_DETAILS.SUCCESSFULLY_SAVED'))

          // return to overview
          this.back(false)
        })
        .catch(error => {
          this.populateFormError(this.$refs.NewsDetailForm, error).catch(this.NotifyError)
        })
        .finally(() => {
          this.loading = false
        })
    },

    /**
     * Add new text content element to list
     */
    addContentElement () {
      this.model.contentElements = this.model.contentElements || []
      this.$set(this.model.contentElements, this.model.contentElements.length, { initialId: this.model.contentElements.length })
    },

    /**
     * Delete selected text content element from list
     *
     * @param index
     */
    deleteContentElement (index) {
      this.$delete(this.model.contentElements, index)
    },

    /**
     * Add new button-action
     */
    addAction () {
      this.model.actions = this.model.actions || []
      this.$set(this.model.actions, this.model.actions.length, { initialId: this.model.actions.length })
    },

    /**
     * Delete selected button-action
     *
     * @param index
     */
    deleteAction (index) {
      if (!this.model._id && Array.isArray(this.model.actions) && this.model.actions.length >= index && this.model.actions[index].action === 'download') {
        this.deleteFile(this.model.actions[index].path)
      }
      this.$delete(this.model.actions, index)

      if (this.errorsEmptyDownloads.indexOf(index) > -1) {
        this.errorsEmptyDownloads = []

        this.model.actions.forEach((action, index) => {
          if (action.action === 'download' && !action.path) {
            this.errorsEmptyDownloads.push(index)
          }
        })
      }
    },

    /**
     * Open file chooser to select file to upload
     *
     * @param index
     */
    openHiddenFileInput (index) {
      let refUpload = this.$refs['hiddenFileInput' + index]

      // reset file input um neue dateien hochladen zu können
      if (Array.isArray(refUpload) && refUpload[0]) {
        refUpload[0].value = ''
        refUpload[0].click()
      }
    },

    /**
     * wird bei onChange aufgerufen, wenn im Dateidialog Dateien ausgewählt werden
     * diese werden an Methode addFiles übergeben, wo sie zerlegt und aufbereitet werden
     */
    addFilesByDialog (index) {
      let refUpload = this.$refs['hiddenFileInput' + index]

      if (Array.isArray(refUpload) && refUpload[0]) {
        let file = refUpload[0]

        if (file && file.files && file.files.length > 0) {
          this.uploadFile(index, file.files[0])
        }
      }
    },

    /**
     * lädt Dateien hoch
     */
    uploadFile (index, file) {
      if (this.uploadingFile) return
      this.uploadingFile = true

      // prepare post request data
      let formData = new FormData()
      formData.append('file', file)

      // post input file to backend
      axios.post('/dashboard/news/action/upload', formData)
        .then((res) => {
          if (res.data && Array.isArray(this.model.actions) && this.model.actions.length >= index) {
            // Remove recent file when it would be replaced and new news
            if (!this.model._id && this.model.actions[index].path) {
              this.deleteFile(this.model.actions[index].path)
            }

            let currentAction = this.model.actions[index]
            currentAction.path = res.data.document_filename || null
            currentAction.filename = file.name || null
            currentAction.status = 'temporary'
            this.$set(this.model.actions, index, currentAction)

            this.errorsEmptyDownloads = []

            this.model.actions.forEach((action, index) => {
              if (action.action === 'download' && !action.path) {
                this.errorsEmptyDownloads.push(index)
              }
            })
          }
        })
        .catch(this.NotifyError)
        .finally(() => {
          this.uploadingFile = false
        })
    },

    /**
     * Delete file
     *
     * 1. News is new and not saved and file is replaced
     * 2. Back is triggered and file is temporary
     * 3. Download-action was deleted from list
     *
     * @param path
     */
    deleteFile (path) {
      if (path) {
        this.$http.delete('/dashboard/news/action/upload', { filename: path })
          .then(() => {})
          .catch(this.NotifyError)
      }
    },

    setErrorElementForEmptyDownload (actionValue, actionIndex) {
      if (actionValue === 'download' && this.errorsEmptyDownloads.indexOf(actionIndex) < 0) {
        this.errorsEmptyDownloads.push(actionIndex)
      }
    },

    /**
     *
     * COLUMNS DRAG AND DROP
     *
     **/

    /**
     * wird zum Start des Verschiebens einer Zeile per Drag-and-Drop aufgerufen
     **/
    startRowDrag (row) {
      this.originalRow = row.initialId
      const color = this.compColor.isInDarkMode ? 'active-move amber lighten-4' : 'active-move amber  lighten-3'

      if (!row.dragDropClass) {
        this.$set(row, 'dragDropClass', color)
      } else {
        row.dragDropClass += color
      }
      this.movedRow = row
    },

    /**
     * prüft beim Drag-and-Drop mit welcher weiteren Spalte, die aktuell verschobene kollidiert und diese damit verschiebt
     * **/
    checkRowCollision (elements, row) {
      if (!this.lockDragEnter && row && this.movedRow && row.initialId !== this.movedRow.initialId && row.initialId !== this.activeHoveredRow) {
        this.lockDragEnter = true
        this.activeHoveredRow = row.initialId

        elements.moveAndRecalcIDs(this.originalRow, this.activeHoveredRow, 'initialId')
        this.originalRow = this.activeHoveredRow
        setTimeout(() => {
          this.lockDragEnter = false
        }, 200)
      }
    },

    /**
     * wird zum Ende des Verschiebens einer Zeile per Drag-and-Drop aufgerufen
     **/
    endRowDrag () {
      if (this.movedRow.dragDropClass) {
        let nwStr = ''
        const color = this.compColor.isInDarkMode ? 'active-move amber lighten-4' : 'active-move amber lighten-3'

        if (this.movedRow.dragDropClass.indexOf(color) >= 0) {
          nwStr = this.movedRow.dragDropClass.replace(color, '')
        }
        this.movedRow.dragDropClass = nwStr
      }
      this.lockDragEnter = false
      this.movedRow = null
      this.originalRow = null
      this.activeHoveredRow = null
    },

    /**
     * Format text of selected role element in upload-popup
     *
     * @param elem
     * @returns {VueI18n.TranslateResult}
     */
    formatRoleListElement (elem) {
      let returnValue = this.$t('DOWNLOADS.ROLES.' + elem.item)

      if (elem.index > 0) {
        returnValue = ', ' + returnValue
      }
      return returnValue
    },

    /**
     * Reset validFromDate, validToDate & roles from model when isWelcomeNews switch toggled to true
     */
    toggleIsWelcomeNews () {
      if (this.model.isWelcomeNews) {
        this.model.validFromDate = null
        this.model.validToDate = null
        this.model.roles = null
      }
    }
  },

  computed: {
    userIsSystemadmin () {
      return this.authStore.getters.userIsSystemadmin
    },
    userIsRoot () {
      return this.authStore.getters.userIsAdmin
    },
    isPristine () {
      if (!this.oldModel) {
        return false
      }
      return JSON.stringify(this.oldModel) === JSON.stringify(this.model)
    }
  }
}
</script>
